import { useCallback, useEffect, useRef, useState } from "react";
import { CreateDiscussionThreadRequest } from "src/service/interaction";
import { convertDateToStringDDMMYYYY } from "src/utils";

import {
  createDiscussionMessage,
  createDiscussionThread,
  deleteDiscussionMessage,
  deleteDiscussionThread,
  updateDiscussionMessage,
  updateDiscussionThread,
} from "../../service/interaction/InteractionService.full";
import { searchDiscussionThreads } from "../../service/query/QueryService.full";
import { ServiceError, Status } from "../../service/Shared";
import { useAuth } from "../../useAuth";
import { Toast } from "../general/Toast";
import type { DiscussionPermissions, DiscussionThreadData } from "./useDiscussionsPanel";

type RawMessage = {
  uuid: string;
  content: string;
  createdAt: Date;
  rowVersion: number;
  createdByUser: { uuid: string; fullName: string | null };
};

type RawThread = {
  uuid: string;
  createdAt: Date;
  isResolved: boolean;
  messages: RawMessage[];
};

const INITIAL_LIMIT = 3;
const PAGE_LIMIT = 5;

const mapRawThread = (raw: RawThread): DiscussionThreadData => {
  const first = raw.messages[0];
  return {
    uuid: raw.uuid,
    authorName: first?.createdByUser?.fullName ?? "",
    authorUuid: first?.createdByUser?.uuid ?? "",
    date: convertDateToStringDDMMYYYY(raw.createdAt),
    content: first?.content ?? "",
    isUnread: false,
    isResolved: raw.isResolved,
    replies: raw.messages.slice(1).map((msg) => ({
      uuid: msg.uuid,
      authorName: msg.createdByUser?.fullName ?? "",
      authorUuid: msg.createdByUser?.uuid ?? "",
      date: convertDateToStringDDMMYYYY(msg.createdAt),
      content: msg.content,
    })),
  };
};

const buildRawThread = (
  uuid: string,
  messageUuid: string,
  content: string,
  authorUuid: string,
  authorName: string | null
): RawThread => {
  const now = new Date();
  return {
    uuid,
    createdAt: now,
    isResolved: false,
    messages: [
      {
        uuid: messageUuid,
        content,
        createdAt: now,
        rowVersion: 1,
        createdByUser: { uuid: authorUuid, fullName: authorName },
      },
    ],
  };
};

export interface UseDiscussionsBaseResult {
  threads: DiscussionThreadData[];
  permissions: DiscussionPermissions;
  currentUserUuid: string;
  hasMoreFromServer: boolean;
  hasMoreResolvedFromServer: boolean;
  onShowMore: () => void;
  onShowMoreResolved: () => void;
  onShowResolvedChange: (value: boolean) => void;
  onAddComment: (content: string) => Promise<void>;
  onReply: (threadUuid: string, content: string) => Promise<void>;
  onEditThreadMessage: (threadUuid: string, content: string) => void;
  onEditReply: (threadUuid: string, replyUuid: string, content: string) => void;
  onDeleteThread: (threadUuid: string) => void;
  onDeleteReply: (threadUuid: string, replyUuid: string) => void;
  onResolveThread: (threadUuid: string) => void;
  errors: ServiceError[];
}

interface UseDiscussionsBaseProps {
  objectUuid: string | undefined;
  objectType: string;
  buildCreateRequest: (
    content: string,
    objectUuid: string,
    currentOrganisationUuid: string
  ) => Promise<CreateDiscussionThreadRequest>;
  resolveTargetOrganisationUuids?: (objectUuid: string, currentOrganisationUuid: string) => Promise<string[] | null>;
}

export const useDiscussionsBase = ({
  objectUuid,
  objectType,
  buildCreateRequest,
  resolveTargetOrganisationUuids,
}: UseDiscussionsBaseProps): UseDiscussionsBaseResult => {
  const { currentOrganisationUuid, user } = useAuth();
  const currentUserUuid = user?.userUuid ?? "";

  const rawMapRef = useRef<Map<string, RawThread>>(new Map());
  const locallyCreatedUuidsRef = useRef<Set<string>>(new Set());

  const [activeThreads, setActiveThreads] = useState<DiscussionThreadData[]>([]);
  const [resolvedThreads, setResolvedThreads] = useState<DiscussionThreadData[]>([]);
  const [activeCursor, setActiveCursor] = useState<string | null>(null);
  const [hasMoreActive, setHasMoreActive] = useState(false);
  const [resolvedCursor, setResolvedCursor] = useState<string | null>(null);
  const [hasMoreResolved, setHasMoreResolved] = useState(false);
  const [resolvedFetched, setResolvedFetched] = useState(false);
  const [errors, setErrors] = useState<ServiceError[]>([]);

  // ---------------------------------------------------------------------------
  // Server fetch / pagination
  // ---------------------------------------------------------------------------

  const fetchActive = useCallback(
    async (afterCursor: string | null, limit: number, replace = false) => {
      if (!objectUuid) return;
      try {
        const response = await searchDiscussionThreads({
          objectType,
          objectUuid,
          isDraft: true,
          includeResolved: false,
          paging: { afterCursor, beforeCursor: null, limit },
          sort: [{ key: "results.createdAt", direction: "desc" }],
        });
        if (response.status !== Status.Success || !response.data) {
          Toast.error({ message: "Failed to load discussions" });
          return;
        }
        const rawThreads = response.data.results as unknown as RawThread[];
        if (replace) locallyCreatedUuidsRef.current.clear();
        const incoming = replace ? rawThreads : rawThreads.filter((t) => !locallyCreatedUuidsRef.current.has(t.uuid));
        incoming.forEach((t) => rawMapRef.current.set(t.uuid, t));
        setActiveThreads((prev) => (replace ? incoming.map(mapRawThread) : [...prev, ...incoming.map(mapRawThread)]));
        setActiveCursor(response.data.paging.endCursor ?? null);
        setHasMoreActive(response.data.paging.hasNextPage);
      } catch {
        Toast.error({ message: "Failed to load discussions" });
      }
    },
    [objectUuid, objectType]
  );

  const fetchResolved = useCallback(
    async (afterCursor: string | null, limit: number, replace = false) => {
      if (!objectUuid) return;
      try {
        const response = await searchDiscussionThreads({
          objectType,
          objectUuid,
          isDraft: true,
          includeResolved: true,
          paging: { afterCursor, beforeCursor: null, limit },
          filter: { results: { isResolved: { operator: "eq", value: true } } },
          sort: [{ key: "results.createdAt", direction: "desc" }],
        });
        if (response.status !== Status.Success || !response.data) {
          Toast.error({ message: "Failed to load resolved discussions" });
          return;
        }
        const rawThreads = response.data.results as unknown as RawThread[];
        rawThreads.forEach((t) => rawMapRef.current.set(t.uuid, t));
        setResolvedThreads((prev) =>
          replace ? rawThreads.map(mapRawThread) : [...prev, ...rawThreads.map(mapRawThread)]
        );
        setResolvedCursor(response.data.paging.endCursor ?? null);
        setHasMoreResolved(response.data.paging.hasNextPage);
      } catch {
        Toast.error({ message: "Failed to load resolved discussions" });
      }
    },
    [objectUuid, objectType]
  );

  const refetch = useCallback(async () => {
    rawMapRef.current.clear();
    await fetchActive(null, INITIAL_LIMIT, true);
    if (resolvedFetched) await fetchResolved(null, INITIAL_LIMIT, true);
  }, [fetchActive, fetchResolved, resolvedFetched]);

  useEffect(() => {
    fetchActive(null, INITIAL_LIMIT, true).catch(() => {
      Toast.error({ message: "Failed to load discussions" });
    });
  }, [fetchActive]);

  const onShowMore = useCallback(() => {
    if (hasMoreActive) {
      fetchActive(activeCursor, PAGE_LIMIT).catch(() => Toast.error({ message: "Failed to load discussions" }));
    }
  }, [hasMoreActive, activeCursor, fetchActive]);

  const onShowMoreResolved = useCallback(() => {
    if (hasMoreResolved) {
      fetchResolved(resolvedCursor, PAGE_LIMIT).catch(() =>
        Toast.error({ message: "Failed to load resolved discussions" })
      );
    }
  }, [hasMoreResolved, resolvedCursor, fetchResolved]);

  const onShowResolvedChange = useCallback(
    (value: boolean) => {
      if (value && !resolvedFetched) {
        setResolvedFetched(true);
        fetchResolved(null, INITIAL_LIMIT).catch(() => Toast.error({ message: "Failed to load resolved discussions" }));
      }
    },
    [resolvedFetched, fetchResolved]
  );

  // ---------------------------------------------------------------------------
  // Mutation handlers
  // ---------------------------------------------------------------------------

  const onAddComment = useCallback(
    async (content: string): Promise<void> => {
      if (!objectUuid || !currentOrganisationUuid) return;
      setErrors([]);
      const params = await buildCreateRequest(content, objectUuid, currentOrganisationUuid);
      const res = await createDiscussionThread(params);
      if (res.status !== Status.Success || !res.data) {
        setErrors(res.errors ?? []);
        throw new Error();
      }
      const newRaw = buildRawThread(
        res.data.threadUuid,
        res.data.messageUuid,
        content,
        currentUserUuid,
        user?.fullName ?? null
      );
      rawMapRef.current.set(res.data.threadUuid, newRaw);
      locallyCreatedUuidsRef.current.add(res.data.threadUuid);
      setActiveThreads((prev) => [mapRawThread(newRaw), ...prev]);
    },
    [objectUuid, currentOrganisationUuid, buildCreateRequest, currentUserUuid, user?.fullName]
  );

  const onReply = useCallback(
    async (threadUuid: string, content: string): Promise<void> => {
      if (!objectUuid || !currentOrganisationUuid) return;
      const raw = rawMapRef.current.get(threadUuid);
      const parentMessageUuid = raw?.messages[raw.messages.length - 1]?.uuid;
      if (!parentMessageUuid) return;
      setErrors([]);
      const targetOrganisationUuids = resolveTargetOrganisationUuids
        ? await resolveTargetOrganisationUuids(objectUuid, currentOrganisationUuid)
        : null;
      const res = await createDiscussionMessage({
        threadUuid,
        parentMessageUuid,
        content,
        messageType: "Comment",
        messageObjectType: objectType,
        messageObjectUuid: objectUuid,
        messageStatus: "Published",
        sourceOrganisationUuid: currentOrganisationUuid,
        targetOrganisationUuids,
      });
      if (res.status !== Status.Success) {
        setErrors(res.errors ?? []);
        throw new Error();
      }
      refetch();
    },
    [objectUuid, objectType, currentOrganisationUuid, resolveTargetOrganisationUuids, refetch]
  );

  const onEditThreadMessage = useCallback((threadUuid: string, content: string) => {
    const raw = rawMapRef.current.get(threadUuid);
    const firstMsg = raw?.messages[0];
    if (!firstMsg || !raw) return;
    setErrors([]);
    updateDiscussionMessage({
      messageUuid: firstMsg.uuid,
      content,
      rowVersion: firstMsg.rowVersion,
      acceptOrReject: null,
      rejectedReason: null,
    })
      .then((res) => {
        if (res.status !== Status.Success) {
          setErrors(res.errors ?? []);
          return;
        }
        rawMapRef.current.set(threadUuid, {
          ...raw,
          messages: [{ ...firstMsg, content, rowVersion: firstMsg.rowVersion + 1 }, ...raw.messages.slice(1)],
        });
        const applyEdit = (t: DiscussionThreadData): DiscussionThreadData =>
          t.uuid === threadUuid ? { ...t, content } : t;
        setActiveThreads((prev) => prev.map(applyEdit));
        setResolvedThreads((prev) => prev.map(applyEdit));
      })
      .catch(() => setErrors([]));
  }, []);

  const onEditReply = useCallback((threadUuid: string, replyUuid: string, content: string) => {
    const raw = rawMapRef.current.get(threadUuid);
    const msg = raw?.messages.find((m) => m.uuid === replyUuid);
    if (!msg || !raw) return;
    setErrors([]);
    updateDiscussionMessage({
      messageUuid: replyUuid,
      content,
      rowVersion: msg.rowVersion,
      acceptOrReject: null,
      rejectedReason: null,
    })
      .then((res) => {
        if (res.status !== Status.Success) {
          setErrors(res.errors ?? []);
          return;
        }
        rawMapRef.current.set(threadUuid, {
          ...raw,
          messages: raw.messages.map((m) =>
            m.uuid === replyUuid ? { ...m, content, rowVersion: m.rowVersion + 1 } : m
          ),
        });
        const applyReplyEdit = (t: DiscussionThreadData): DiscussionThreadData =>
          t.uuid !== threadUuid
            ? t
            : { ...t, replies: t.replies.map((r) => (r.uuid === replyUuid ? { ...r, content } : r)) };
        setActiveThreads((prev) => prev.map(applyReplyEdit));
        setResolvedThreads((prev) => prev.map(applyReplyEdit));
      })
      .catch(() => setErrors([]));
  }, []);

  const onDeleteThread = useCallback((threadUuid: string) => {
    setErrors([]);
    deleteDiscussionThread({ threadUuid })
      .then((res) => {
        if (res.status !== Status.Success) {
          setErrors(res.errors ?? []);
          return;
        }
        rawMapRef.current.delete(threadUuid);
        setActiveThreads((prev) => prev.filter((t) => t.uuid !== threadUuid));
        setResolvedThreads((prev) => prev.filter((t) => t.uuid !== threadUuid));
      })
      .catch(() => setErrors([]));
  }, []);

  const onDeleteReply = useCallback(
    (_threadUuid: string, replyUuid: string) => {
      setErrors([]);
      deleteDiscussionMessage({ messageUuid: replyUuid })
        .then((res) => {
          if (res.status !== Status.Success) {
            setErrors(res.errors ?? []);
            return;
          }
          refetch();
        })
        .catch(() => setErrors([]));
    },
    [refetch]
  );

  const onResolveThread = useCallback(
    (threadUuid: string) => {
      setErrors([]);
      updateDiscussionThread({ threadUuid, isResolved: true, rowVersion: 1 })
        .then((res) => {
          if (res.status !== Status.Success) {
            setErrors(res.errors ?? []);
            return;
          }
          const raw = rawMapRef.current.get(threadUuid);
          if (raw) {
            const resolvedRaw = { ...raw, isResolved: true };
            rawMapRef.current.set(threadUuid, resolvedRaw);
            if (resolvedFetched) setResolvedThreads((prev) => [mapRawThread(resolvedRaw), ...prev]);
          }
          setActiveThreads((prev) => prev.filter((t) => t.uuid !== threadUuid));
        })
        .catch(() => setErrors([]));
    },
    [resolvedFetched]
  );

  const permissions: DiscussionPermissions = { createResource: true };

  return {
    threads: [...activeThreads, ...resolvedThreads],
    permissions,
    currentUserUuid,
    hasMoreFromServer: hasMoreActive,
    hasMoreResolvedFromServer: hasMoreResolved,
    onShowMore,
    onShowMoreResolved,
    onShowResolvedChange,
    onAddComment,
    onReply,
    onEditThreadMessage,
    onEditReply,
    onDeleteThread,
    onDeleteReply,
    onResolveThread,
    errors,
  };
};
